import { useRef, useState } from 'react';
import { X, Download, Share2, Copy, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

const TasteProfileShareCard = ({ profile, user, onClose }) => {
  const cardRef = useRef(null);
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const topCuisines = profile?.top_cuisines?.slice(0, 3) || [];
  const shareText = `🍽️ My FoodQuest food personality: "${profile?.badge}" ${profile?.emoji || ''}
${profile?.description || ''}
Top cuisines: ${topCuisines.join(' · ')}
Discover yours at FoodQuest!`;

  const handleCopyText = async () => {
    try {
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(cardRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: null,
        logging: false,
      });
      const link = document.createElement('a');
      link.download = `foodquest-taste-${(profile?.badge || 'profile').toLowerCase().replace(/\s+/g, '-')}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (err) {
      console.error('Download failed:', err);
    } finally {
      setDownloading(false);
    }
  };

  const handleNativeShare = async () => {
    if (!navigator.share) return;
    try {
      await navigator.share({ title: 'My FoodQuest Taste Profile', text: shareText });
    } catch { /* user cancelled */ }
  };

  const CUISINE_COLORS = [
    'bg-orange-400', 'bg-red-400', 'bg-yellow-400',
    'bg-green-400', 'bg-blue-400', 'bg-purple-400',
  ];

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
        {/* Close */}
        <button onClick={onClose} className="absolute top-3 right-3 z-10 text-white/70 hover:text-white bg-black/20 rounded-full p-1.5">
          <X className="w-4 h-4" />
        </button>

        {/* Share Card (this gets captured) */}
        <div
          ref={cardRef}
          className="relative p-8 text-white text-center overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, #F4C430 0%, #FF8C42 40%, #FF6B35 70%, #E85D04 100%)',
            minHeight: '360px',
          }}
        >
          {/* Decorative circles */}
          <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2" />

          {/* FoodQuest brand */}
          <p className="text-white/70 text-xs font-medium tracking-widest uppercase mb-6 relative z-10">FoodQuest · Food Personality</p>

          {/* Emoji */}
          <div className="text-7xl mb-4 relative z-10 drop-shadow-lg">{profile?.emoji || '🍽️'}</div>

          {/* Badge */}
          <h2 className="text-3xl font-extrabold mb-2 relative z-10 leading-tight">{profile?.badge || 'Food Explorer'}</h2>

          {/* Description */}
          <p className="text-white/90 text-sm mb-6 relative z-10 max-w-xs mx-auto leading-relaxed">
            {profile?.description || 'A passionate food explorer on a global culinary journey.'}
          </p>

          {/* Top Cuisines */}
          {topCuisines.length > 0 && (
            <div className="flex flex-wrap justify-center gap-2 mb-6 relative z-10">
              {topCuisines.map((c, i) => (
                <span key={c} className={`${CUISINE_COLORS[i % CUISINE_COLORS.length]} text-white text-xs px-3 py-1 rounded-full font-semibold`}>
                  {c}
                </span>
              ))}
            </div>
          )}

          {/* Stats row */}
          <div className="flex justify-center gap-6 relative z-10 text-white/90">
            <div className="text-center">
              <div className="text-xl font-bold">{profile?.total_saves || 0}</div>
              <div className="text-xs">Saved</div>
            </div>
            <div className="w-px bg-white/30" />
            <div className="text-center">
              <div className="text-xl font-bold">{profile?.total_searches || 0}</div>
              <div className="text-xs">Searches</div>
            </div>
            <div className="w-px bg-white/30" />
            <div className="text-center">
              <div className="text-xl font-bold capitalize">{profile?.personality || 'explorer'}</div>
              <div className="text-xs">Type</div>
            </div>
          </div>

          {/* User */}
          {user?.name && (
            <p className="text-white/60 text-xs mt-4 relative z-10">@{user.username?.replace('@', '') || user.name}</p>
          )}
        </div>

        {/* Actions */}
        <div className="p-4 space-y-2 bg-white">
          <p className="text-center text-xs text-gray-500 mb-3">Share your food personality with the world!</p>
          <div className="grid grid-cols-3 gap-2">
            <Button
              data-testid="share-download-btn"
              onClick={handleDownload}
              disabled={downloading}
              variant="outline"
              className="flex flex-col h-16 gap-1 border-gray-200 hover:border-[#FF6B35] hover:text-[#FF6B35]"
            >
              <Download className={`w-5 h-5 ${downloading ? 'animate-bounce' : ''}`} />
              <span className="text-xs">{downloading ? 'Saving...' : 'Download'}</span>
            </Button>

            <Button
              data-testid="share-copy-btn"
              onClick={handleCopyText}
              variant="outline"
              className="flex flex-col h-16 gap-1 border-gray-200 hover:border-[#FF6B35] hover:text-[#FF6B35]"
            >
              {copied ? <Check className="w-5 h-5 text-green-500" /> : <Copy className="w-5 h-5" />}
              <span className="text-xs">{copied ? 'Copied!' : 'Copy Text'}</span>
            </Button>

            {navigator.share ? (
              <Button
                data-testid="share-native-btn"
                onClick={handleNativeShare}
                className="flex flex-col h-16 gap-1 bg-gradient-to-r from-[#F4C430] to-[#FF6B35] text-white"
              >
                <Share2 className="w-5 h-5" />
                <span className="text-xs">Share</span>
              </Button>
            ) : (
              <Button
                variant="outline"
                className="flex flex-col h-16 gap-1 border-gray-200 text-gray-400"
                disabled
              >
                <Share2 className="w-5 h-5" />
                <span className="text-xs">Share</span>
              </Button>
            )}
          </div>
          <p className="text-center text-xs text-gray-400 mt-2">Screenshot the card above to share on Instagram or TikTok</p>
        </div>
      </div>
    </div>
  );
};

export default TasteProfileShareCard;
